import {
  useCallback,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { UnsavedChangesPopup } from '@/components/shared/unsaved-changes-popup';
import {
  SettingsUnsavedChangesContext,
  type UnsavedGuard,
} from './SettingsUnsavedChangesContext';

/**
 * Provides the settings-wide "discard unsaved changes?" guard (spec KIR-3253
 * #14). A panel registers a guard; the tab bar routes navigation through
 * `confirmNavigation`, which shows the discard dialog when the guard reports
 * unsaved changes.
 */
export function SettingsUnsavedChangesProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const guardRef = useRef<UnsavedGuard | null>(null);
  const pendingRef = useRef<(() => void) | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const registerGuard = useCallback((guard: UnsavedGuard | null): void => {
    guardRef.current = guard;
  }, []);

  const confirmNavigation = useCallback((proceed: () => void): void => {
    if (guardRef.current?.()) {
      pendingRef.current = proceed;
      setIsDialogOpen(true);
      return;
    }
    proceed();
  }, []);

  const handleDiscard = useCallback((): void => {
    const proceed = pendingRef.current;
    pendingRef.current = null;
    // The panel that owned the changes is about to unmount; clear its guard so
    // a stale check can't re-trigger before the unmount cleanup runs.
    guardRef.current = null;
    setIsDialogOpen(false);
    proceed?.();
  }, []);

  const handleCancel = useCallback((): void => {
    pendingRef.current = null;
    setIsDialogOpen(false);
  }, []);

  return (
    <SettingsUnsavedChangesContext.Provider
      value={{ registerGuard, confirmNavigation }}
    >
      {children}
      <UnsavedChangesPopup
        open={isDialogOpen}
        onDiscard={handleDiscard}
        onCancel={handleCancel}
      />
    </SettingsUnsavedChangesContext.Provider>
  );
}
